// Renders the chat's turn list. User turns are compact bubbles;
// assistant turns render streaming text + inline tool-call cards.

import { useEffect, useRef } from 'react';
import { Box, VStack, HStack, Text, Spinner, Avatar, Badge, SimpleGrid, Image, Progress } from '@chakra-ui/react';
import { SafeMarkdown } from '../components/safeMarkdown';
import type { PlanBriefing } from './briefing';
import type { AssistantTurn, ChatAttachmentPreview, ChatTurn, ToolCallRecord, WorkflowProgressEvent } from './types';
import { ResourceCard } from './ResourceCard';

// After this many milliseconds without an event from the server we
// flag the turn as stalled and surface a "still waiting" banner. The
// server sends SSE keep-alive comments every 15s to hold proxies
// open, so anything > 30s reliably indicates a real stall.
const STALL_THRESHOLD_MS = 30_000;

type Props = {
  turns: ChatTurn[];
};

export function MessageList({ turns }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  if (!turns.length) return null;

  return (
    <Box ref={scrollRef} overflowY="auto" flex="1" minH={0} px={{ base: 3, md: 0 }}>
      <VStack align="stretch" spacing={5} maxW="760px" mx="auto" py={4}>
        {turns.map((t) => {
          if (t.kind === 'user') return <UserBubble key={t.id} content={t.content} previews={t.previews} />;
          if (t.kind === 'briefing') return <BriefingView key={t.id} briefing={t.briefing} />;
          return <AssistantTurnView key={t.id} turn={t.turn} />;
        })}
      </VStack>
    </Box>
  );
}

function BriefingView({ briefing }: { briefing: PlanBriefing }) {
  if (!briefing) return null;
  return (
    <HStack align="flex-start" spacing={3} data-testid="plan-briefing">
      <Avatar size="xs" name="Agent" bg="brand.primary" color="white" />
      <VStack align="stretch" spacing={2} flex="1" minW={0}>
        <Text fontSize="14px" fontWeight="700">Plan briefing</Text>
        {briefing.generatedLabel && (
          <Text fontSize="12px" color="brand.muted">Updated {briefing.generatedLabel}</Text>
        )}
        {briefing.unavailableReason && (
          <Text fontSize="14px" data-testid="briefing-unavailable">{briefing.unavailableReason}</Text>
        )}
        {briefing.plans.map((plan, index) => (
          <VStack key={`${plan.title}-${index}`} align="stretch" spacing={1} data-testid="briefing-plan">
            <Text fontSize="14px" fontWeight="600">
              {[plan.title, plan.revision ? `Revision ${plan.revision}` : null, plan.status].filter(Boolean).join(' · ')}
            </Text>
            {plan.pacing && (
              <Text fontSize="14px">Pacing {plan.pacing}{plan.pacingDetail ? `. ${plan.pacingDetail}` : ''}</Text>
            )}
            {plan.tests.map((test, testIndex) => (
              <Text key={`${test.name}-${testIndex}`} fontSize="14px">
                {test.name}{test.callLabel ? `: ${test.callLabel}` : ''}
              </Text>
            ))}
            {plan.gaps.map((gap, gapIndex) => <Text key={`${gap}-${gapIndex}`} fontSize="14px">Gap: {gap}</Text>)}
            {plan.approvals.map((approval, approvalIndex) => <Text key={`${approval}-${approvalIndex}`} fontSize="14px">Approval: {approval}</Text>)}
            {plan.stock.map((flag, stockIndex) => <Text key={`${flag}-${stockIndex}`} fontSize="14px">Stock: {flag}</Text>)}
          </VStack>
        ))}
        {briefing.integrations.map((line) => (
          <Text key={line} fontSize="14px" data-testid="briefing-integration">{line}</Text>
        ))}
        {briefing.nextAction && (
          <Text fontSize="14px" fontWeight="600" data-testid="briefing-next">Next: {briefing.nextAction}</Text>
        )}
      </VStack>
    </HStack>
  );
}

function UserBubble({ content, previews }: { content: string; previews?: ChatAttachmentPreview[] }) {
  const hasPreviews = Array.isArray(previews) && previews.length > 0;
  return (
    <HStack align="flex-start" spacing={3} justify="flex-end">
      <Box
        maxW="70%"
        px={4} py={2}
        bg="brand.surface"
        borderRadius="18px"
        borderTopRightRadius="4px"
      >
        {hasPreviews && (
          <HStack spacing={1.5} mb={content ? 2 : 0} flexWrap="wrap">
            {previews!.map((p, i) => (
              <Box
                key={i}
                w="56px"
                h="56px"
                borderRadius="sm"
                overflow="hidden"
                borderWidth="1px"
                borderColor="brand.border"
                title={p.filename}
              >
                <Image src={p.previewUrl} w="100%" h="100%" objectFit="cover" fallbackSrc="" />
              </Box>
            ))}
          </HStack>
        )}
        {content && <Text fontSize="14px" whiteSpace="pre-wrap">{content}</Text>}
      </Box>
    </HStack>
  );
}

function AssistantTurnView({ turn }: { turn: AssistantTurn }) {
  return (
    <HStack align="flex-start" spacing={3}>
      <Avatar size="xs" name="Agent" bg="brand.primary" color="white" />
      <VStack align="stretch" spacing={2} flex="1" minW={0}>
        {turn.thinking && <ThinkingReveal thinking={turn.thinking} streaming={turn.streaming} />}
        {turn.content && (
          <Box>
            <SafeMarkdown text={turn.content} fontSize="14px" testId="assistant-markdown" tableTestId="assistant-markdown-table" />
            {turn.streaming && <StreamingCursor />}
          </Box>
        )}
        {turn.toolCalls.map((tc) => <ToolCallRow key={tc.toolCallId} record={tc} />)}
        {turn.streaming && !turn.content && !turn.toolCalls.length && (
          <ThinkingIndicator turn={turn} />
        )}
      </VStack>
    </HStack>
  );
}

// Collapsed disclosure of the model's reasoning tokens. Uses a native
// <details> element so keyboard/AT semantics are free and no extra state
// hook is needed. Rendered only when the stream carried any thinking
// deltas — Gemini flash and Claude extended-thinking do, gpt-4o-mini does
// not, and the block should be silent in that case rather than an empty
// disclosure. While streaming we show a subtle spinner next to the label
// so the operator can see reasoning is actively arriving.
function ThinkingReveal({ thinking, streaming }: { thinking: string; streaming: boolean }) {
  return (
    <Box
      as="details"
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="md"
      px={3}
      py={2}
      bg="brand.surface"
    >
      <Box
        as="summary"
        cursor="pointer"
        fontSize="11px"
        color="brand.muted"
        sx={{ listStyle: 'none', '&::-webkit-details-marker': { display: 'none' } }}
      >
        <HStack spacing={2}>
          {streaming && <Spinner size="xs" />}
          <Text as="span">
            {streaming ? 'Thinking…' : 'Reasoning'} <Text as="span" color="brand.muted">· click to {streaming ? 'watch' : 'expand'}</Text>
          </Text>
        </HStack>
      </Box>
      <Text
        mt={2}
        fontSize="12px"
        color="brand.muted"
        whiteSpace="pre-wrap"
        fontFamily="mono"
      >
        {thinking}
      </Text>
    </Box>
  );
}

function ThinkingIndicator({ turn }: { turn: AssistantTurn }) {
  const now = Date.now();
  const elapsedS = Math.floor((now - turn.startedAt) / 1000);
  const stalledMs = now - turn.lastEventAt;
  const stalled = stalledMs > STALL_THRESHOLD_MS;
  // Description of what phase we're in — best-effort inference from
  // whatever events we've seen so far. The tool-call row handles the
  // per-tool spinners; this is the pre-first-tool "we're waiting on
  // the LLM to decide what to do" state.
  const label = turn.iteration > 1
    ? `Working (iteration ${turn.iteration})…`
    : 'Thinking…';
  return (
    <VStack align="stretch" spacing={1}>
      <HStack spacing={2} fontSize="12px" color={stalled ? 'orange.700' : 'brand.muted'}>
        <Spinner size="xs" color={stalled ? 'orange.500' : undefined} />
        <Text>{label}</Text>
        <Badge fontSize="10px" variant="subtle">{elapsedS}s</Badge>
        {turn.iteration > 0 && (
          <Text color="brand.muted">· LLM iteration {turn.iteration}</Text>
        )}
      </HStack>
      {stalled && (
        <Box p={2} borderWidth="1px" borderColor="orange.300" bg="orange.50" borderRadius="md">
          <Text fontSize="11px" color="orange.700">
            No response in {Math.round(stalledMs / 1000)}s. The model may be slow, or the connection may have dropped. Try again if this keeps stalling.
          </Text>
        </Box>
      )}
    </VStack>
  );
}

function ToolCallRow({ record }: { record: ToolCallRecord }) {
  // In-flight indicator while the server is executing (before we have a
  // result AND before we know it was gated).
  if (record.status === 'started' || record.status === 'complete') {
    return (
      <HStack spacing={2} fontSize="12px" color="brand.muted">
        <Spinner size="xs" />
        <Text>Calling <code>{record.toolName.replace(/__/g, '.')}</code>…</Text>
      </HStack>
    );
  }
  // Workflow progress accumulator during Tier 4 execute — renders a
  // live tile grid so the operator watches cells fill in as they
  // complete (mirrors the ChatGPT image-gen UX). Placeholders show
  // for every planned cell up front; each workflow-progress event
  // populates its cell's tile with the generated image + caption, or
  // a failure marker when the outcome is not 'ok'.
  if (record.progress && record.progress.length > 0 && record.status !== 'result') {
    return <LiveWorkflowGrid progress={record.progress} />;
  }
  // Terminal states — result + gated proposals are rendered by
  // ResourceCard / ConfirmationCard (the latter lives at the container
  // level so a confirm click routes to the hook). Here we render the
  // result once available.
  if (record.result) {
    return <ResourceCard result={record.result} />;
  }
  return null;
}

// Live tile grid — one placeholder per planned cell up front, tiles
// fill in as workflow-progress events arrive with their generated
// image URLs. Cells that failed dim + show the reason. Header carries
// the running count + last-cell breadcrumb.
function LiveWorkflowGrid({ progress }: { progress: WorkflowProgressEvent[] }) {
  const last = progress[progress.length - 1];
  const totalSteps = Number(last.totalSteps) || progress.length;
  const processed = progress.length;
  const pct = totalSteps > 0 ? Math.round((processed / totalSteps) * 100) : 0;

  // Index cells by step (1-based). A progress event's step is its
  // slot in the plan — an event replaces its tile in place, so tiles
  // stay stable as more arrive.
  const byStep = new Map<number, WorkflowProgressEvent>();
  for (const p of progress) {
    const step = Number((p as { step?: number }).step);
    if (Number.isFinite(step)) byStep.set(step, p);
  }
  const succeeded = progress.filter(p => (p as { outcome?: string }).outcome === 'ok').length;
  const failed    = processed - succeeded;

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={2} fontSize="12px" color="brand.muted">
        <Spinner size="xs" />
        <Text fontWeight="600">Generating {totalSteps} cell{totalSteps === 1 ? '' : 's'}</Text>
        <Text>· {processed}/{totalSteps} ({pct}%)</Text>
        {succeeded > 0 && <Text>· {succeeded} ok</Text>}
        {failed > 0    && <Text color="red.600">· {failed} failed</Text>}
      </HStack>
      <Progress value={pct} size="xs" colorScheme="purple" borderRadius="sm" />
      {last && (last as { productName?: string }).productName && (
        <Text fontSize="10px" color="brand.muted" noOfLines={1}>
          <em>Last:</em> {(last as { productName?: string }).productName}
          {(last as { personaName?: string }).personaName && ` · ${(last as { personaName?: string }).personaName}`}
        </Text>
      )}
      <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={1.5} mt={1}>
        {Array.from({ length: totalSteps }, (_, i) => {
          const step = i + 1;
          const cell = byStep.get(step);
          return <LiveTile key={step} cell={cell} />;
        })}
      </SimpleGrid>
    </VStack>
  );
}

function LiveTile({ cell }: { cell: WorkflowProgressEvent | undefined }) {
  // Empty tile (placeholder) — cell hasn't been reached yet
  if (!cell) {
    return (
      <Box
        aspectRatio={1}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="brand.border"
        bg="brand.canvas"
        sx={{ animation: 'agent-tile-pulse 1.6s ease-in-out infinite', '@keyframes agent-tile-pulse': { '50%': { opacity: 0.4 } } }}
      />
    );
  }
  const outcome = (cell as { outcome?: string }).outcome;
  const isOk = outcome === 'ok';
  const imageUrl = (cell as { lifestyleUrl?: string | null; renderUrl?: string | null }).lifestyleUrl
                || (cell as { lifestyleUrl?: string | null; renderUrl?: string | null }).renderUrl
                || null;
  const productName = (cell as { productName?: string }).productName;
  const personaName = (cell as { personaName?: string }).personaName;
  const captionTitle = [productName, personaName].filter(Boolean).join(' · ') || `Step ${(cell as { step?: number }).step}`;

  if (isOk && imageUrl) {
    return (
      <Box
        aspectRatio={1}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="brand.border"
        overflow="hidden"
        cursor="pointer"
        onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
        title={captionTitle}
        _hover={{ borderColor: 'purple.400' }}
      >
        <Image src={imageUrl} objectFit="cover" w="100%" h="100%" fallbackSrc="" />
      </Box>
    );
  }
  // Failed cell — dim + short reason label
  return (
    <Box
      aspectRatio={1}
      borderRadius="sm"
      borderWidth="1px"
      borderColor="red.200"
      bg="red.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={1}
      title={`${captionTitle} · ${outcome}`}
    >
      <Text fontSize="9px" color="red.700" textAlign="center" noOfLines={3}>
        {String(outcome || 'failed').slice(0, 40)}
      </Text>
    </Box>
  );
}

function StreamingCursor() {
  // A tiny blinking █ appended to streaming assistant text. Chakra's
  // keyframes API is heavy for this; inline animation is fine.
  return (
    <Box as="span" ml={1} display="inline-block" width="8px" height="14px" bg="brand.primary" verticalAlign="middle" sx={{
      animation: 'agent-cursor-blink 1s steps(2, start) infinite',
      '@keyframes agent-cursor-blink': { to: { visibility: 'hidden' } }
    }} />
  );
}
